import { AppThunk } from '@lib/state/types';
import { OrderActions } from '@lib/state/_actions';
import {
    OrderPost,
    OrderCheck,
    ServiceCheck,
    ActionTypes,
    OrderInfo,
    OrderCompletePayload,
    OrderSubscriptionDiscountRequest,
} from '@lib/domain/order';
import {
    ListOrderRespone,
    GetListOrderPayload,
    CheckOrderRequest,
} from '@lib/state/Order/types';
import { OrderReducer } from '@lib/state/reducers';

/**
 * Create Order
 * @param {OrderPost} orders
 * @return {AppThunk<Promise<OrderInfo>>}
 */
export const createOrder =
    (orders: OrderPost): AppThunk<Promise<OrderInfo>> =>
    async (dispatch, _getState, container): Promise<OrderInfo> => {
        dispatch(OrderActions.setLoading(true));
        // create order
        const res: OrderInfo =
            await container.cradle.orderRepository.createOrder({
                ...orders,
            });
        // set order to redux state
        // dispatch(OrderActions.addOrderIdNotLoggedIn(res.id));
        dispatch(OrderActions.setCheckoutOrderId(res.id));
        dispatch(OrderActions.createOrder(res, true));
        dispatch(OrderActions.setLoading(false));
        return res;
    };

/**
 * Get list order with token
 *
 * @returns {AppThunk<Promise<void>>}
 */

export const getListOrder =
    (pageInfo: GetListOrderPayload): AppThunk<Promise<void>> =>
    async (dispatch, _getState, container): Promise<void> => {
        try {
            // get list order
            const orders: ListOrderRespone =
                await container.cradle.orderRepository.getListOrder(pageInfo);
            // set order list to redux state
            dispatch(OrderActions.getOrders(orders));
        } catch (error) {
            const { message } = error as Error;
            if (
                typeof message === 'string' &&
                message.includes(
                    ' it must be a 12-byte input or a 24-character hex string',
                )
            ) {
                const orderIds = OrderReducer.selectors.getOrderIds(
                    OrderReducer.getReducerState(_getState()),
                );
                const newOrders: string[] = [];
                orderIds.forEach((id) => {
                    if (id.length >= 24) {
                        newOrders.push(id);
                    }
                });
                dispatch(OrderActions.addOrderIdNotLoggedIn(newOrders));
            }
            throw error;
        }
    };

/**
 * Check list order
 * @return {AppThunk<Promise<OrderCheck[]>>}
 */

export const checkOrders =
    (
        payload: CheckOrderRequest[],
        // test?: boolean,
    ): AppThunk<Promise<OrderCheck[]>> =>
    async (dispatch, _getState, container): Promise<OrderCheck[]> => {
        // Check list orders
        const checkedList = await Promise.all(
            payload.map((item: CheckOrderRequest) =>
                container.cradle.orderRepository.checkOrder(item),
            ),
        );
        let mergeValue: OrderCheck[] = [];
        const mapListOrder = {} as Record<ActionTypes, OrderCheck>;
        const servicesList = [] as ServiceCheck[];
        checkedList.forEach(({ services, in_progress }) => {
            mergeValue.push(...in_progress);
            servicesList.push(...services);
        });
        if (servicesList.length > 0) {
            servicesList.forEach((item: ServiceCheck) => {
                if (!mapListOrder[item.action_type] && !item.enabled) {
                    mapListOrder[item.action_type] = {
                        url: '',
                        action_type: item.action_type,
                        count: 0,
                    };
                }
            });
            if (mergeValue.length > 0) {
                mergeValue.forEach((item: OrderCheck) => {
                    mapListOrder[item.action_type] = item;
                });
            }
            if (Object.keys(mapListOrder).length !== mergeValue.length) {
                mergeValue = Object.keys(mapListOrder).map(
                    (key) => mapListOrder[key as ActionTypes],
                );
            }
        }
        dispatch(OrderActions.checkOrder(mergeValue));
        return mergeValue;
    };

/**
 * Check list order
 * @return {AppThunk<Promise<OrderCheck[]>>}
 */

export const checkPosts =
    (
        payload: CheckOrderRequest[],
        // isTest?: boolean,
    ): AppThunk<Promise<Record<string, Record<ActionTypes, number>>>> =>
    async (
        dispatch,
        _getState,
        container,
    ): Promise<Record<string, Record<ActionTypes, number>>> => {
        // Check list orders
        const checkedList = await Promise.all(
            payload.map((item: CheckOrderRequest) =>
                container.cradle.orderRepository.checkOrder(item),
            ),
        );
        const mergeValue: Record<string, any> = {};
        checkedList.forEach(({ in_progress }) => {
            if (in_progress.length > 0) {
                in_progress.forEach((checkPost) => {
                    if (!mergeValue[checkPost.url]) {
                        mergeValue[checkPost.url] = {};
                    }
                    mergeValue[checkPost.url][checkPost.action_type] =
                        checkPost.count;
                });
            }
        });
        // if (isTest) {
        //     mergeValue = {
        //         'https://www.instagram.com/p/CIzAZFBhOXu/': {
        //             like: 1,
        //         },
        //     };
        // }
        dispatch(OrderActions.setCheckPosts(mergeValue));
        return mergeValue;
    };

/**
 * complete order
 * @returns {AppThunk<Promise<void>>}
 */
export const completeOrder =
    (
        payload: OrderCompletePayload,
        // isTest?: boolean,
    ): AppThunk<Promise<void>> =>
    async (dispatch, _getState, container): Promise<void> => {
        const res = await container.cradle.orderRepository.completeOrder(
            payload,
        );
        dispatch(OrderActions.addOrderIdNotLoggedIn(res.id));
        dispatch(OrderActions.setIsNewOrder(true));
    };

const OrderThunks = {
    createOrder,
    getListOrder,
    checkOrders,
    checkPosts,
    completeOrder,
};

export default OrderThunks;
